/**
 * The pricing table, served by the API so the prices here and the prices the
 * checkout charges cannot drift apart.
 *
 * Nothing is seat-capped today. The seats line still renders when a plan
 * carries a cap, because a cap may come back and a stale "3 seats open" would
 * be a lie the moment someone else bought one.
 *
 * **When billing is switched off the plans still render and the buttons are
 * disabled with a reason.** `/api/plans` serves `billing_enabled`, which is
 * false whenever no Stripe key is configured — the documented "off" state, and
 * a working one. Without this the table advertised a $29 plan whose button
 * opened a 503, which is worse than saying so: the rail's own rule is that a
 * control is **disabled with a reason, never hidden**, and a price a visitor
 * cannot act on is exactly a case for it.
 */

import { useEffect, useState } from "react";

import { api } from "../api/client";
import type { Plan, TierId } from "../api/types";
import { trialLine } from "../lib/billing";

interface Props {
  current: TierId;
  onChoose: (tier: TierId) => void;
  busyTier?: TierId | null;
  /**
   * Whether the reader already pays. Only the wording depends on it: someone
   * with a subscription is switching an existing one, not choosing a first,
   * and "Choose State" above a confirmation that charges the *difference*
   * describes the wrong transaction.
   */
  subscribed?: boolean;
}

export function Plans({ current, onChoose, busyTier, subscribed }: Props) {
  const [plans, setPlans] = useState<Plan[]>([]);
  // Defaults to true so the table never flashes "not open yet" while loading —
  // the same reason `scoreScope` stays quiet on an absent signal list.
  const [billingOpen, setBillingOpen] = useState(true);

  useEffect(() => {
    const controller = new AbortController();
    api
      .plans(controller.signal)
      .then((response) => {
        setPlans(response.plans);
        setBillingOpen(response.billing_enabled);
      })
      .catch(() => {
        /* the upsell is not worth an error banner over */
      });
    return () => controller.abort();
  }, []);

  return (
    <div className="plans">
      {!billingOpen && (
        <p className="plans-closed">
          Subscriptions are not open yet. These are the plans as they will be
          priced; nothing can be bought today.
        </p>
      )}
      {plans.map((plan) => {
        const soldOut = plan.seats_left === 0;
        return (
          <div className="plan" key={plan.id}>
            <div className="eyebrow">{plan.name}</div>
            <div className="price">
              {plan.price_usd ? `$${plan.price_usd}` : "Free"}
              {plan.price_usd > 0 && <small>/mo</small>}
            </div>
            {/* Not to someone already paying: they are switching, not starting. */}
            {!subscribed && trialLine(plan.trial_days) && (
              <div className="trial">
                {trialLine(plan.trial_days, plan.report_credits)}
              </div>
            )}

            {plan.seat_cap !== null && (
              <div className="seats">
                {soldOut
                  ? "Full"
                  : `${plan.seats_left} of ${plan.seat_cap} seats open`}
              </div>
            )}

            <p>{plan.blurb}</p>


            {plan.id === current ? (
              <button className="btn ghost" disabled>
                Current plan
              </button>
            ) : (
              plan.price_usd > 0 && (
                <button
                  className="btn"
                  disabled={!billingOpen || soldOut || busyTier === plan.id}
                  onClick={() => onChoose(plan.id)}
                >
                  {!billingOpen
                    ? "Not open yet"
                    : busyTier === plan.id
                      ? subscribed
                        ? "Working out the difference…"
                        : "Opening…"
                      : soldOut
                        ? "Waitlist"
                        : subscribed
                          ? `Switch to ${plan.name}`
                          : `Choose ${plan.name}`}
                </button>
              )
            )}
          </div>
        );
      })}
    </div>
  );
}
